import cx from 'classnames';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { ELSModalServiceType } from 'models/els';
import {
  AppMessage,
  DATE_FORMAT,
  DATE_FORMAT_MAR,
  DATE_TIME_FORMAT_MAR,
  DateFormatByLocale,
  DateTimeFormatByLocale,
  Locales,
  MOMENT_INCLUSIVITY_ALL,
  MOMENT_UNIT_DAYS
} from 'constants/app.constant';
import { formatDate, toMomentWithFormat, toMomentWithParsers } from 'helpers/datetime.helper';
import { Box, ClinicalKeyDrugInformation, HorizontalScrollableContainer } from 'components/common';
import { ELSButton, ELSDataTable, ELSIcon, ELSTooltip } from 'components/common/els';
import DosageTimesModal from 'components/common/modal/DosageTimesModal';
import { INACTIVE_ORDERS_SHOW_DOSAGE_TIMES_COUNT, INACTIVE_TYPE, ORDER_TYPES } from './constants';
import DrugNameBtnRedirect from './DrugNameBtnRedirect';
import dosageTimesMapping from './dosageTimesMapping';
import { convertDateFormat, getIsShowAdmin, sortByDosageTime } from './helper';

export interface MarTableProps {
  viewDate: string;
  columnHeaderCss?: string[];
  data: object[];
  hideFrequency?: boolean;
  hideAction?: boolean;
  hideStatus?: boolean;
  onDosageClick?: Function;
  handleHoldMedication?: Function;
  handleUnHoldMedication?: Function;
  handleDiscontinueMedication?: Function;
  hideAdministration: boolean;
  locale: Locales;
  isShowEHRMar?: boolean;
  chartingTime?: string;
  getCount?: Function;
  modalService?: ELSModalServiceType;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const MarTable = (props: MarTableProps) => {
  const {
    columnHeaderCss,
    data,
    hideFrequency,
    hideAction,
    hideStatus,
    handleHoldMedication,
    handleDiscontinueMedication,
    handleUnHoldMedication,
    viewDate,
    locale,
    isShowEHRMar,
    chartingTime,
    getCount,
    modalService
  } = props;

  const dateFormatMar = useMemo(() => (isShowEHRMar ? DATE_FORMAT_MAR : undefined), [isShowEHRMar]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const dosageTimesRender = (row) => {
    const { inactiveType, isDiscontinued, orderType, dosageTimes, frequency, isAnyAdministered } = row;
    const isInactiveOrder = inactiveType || isDiscontinued;
    const result = dosageTimes
      ?.filter((item) => row.inactiveType !== INACTIVE_TYPE.COMPLETED || item.dosageTime !== 'Administer')
      .slice(0, isInactiveOrder ? INACTIVE_ORDERS_SHOW_DOSAGE_TIMES_COUNT : undefined)
      .map((item) => {
        const { isAdministered, id, administeredDate, administeredTime, firstInitial, lastInitial, dosageDate, dosageTime } = item;
        const { isHeld, holdTime, holdTimeOffset, holdFirstInitial, holdLastInitial } = item;
        const administeredDateDisplay = formatDate({
          date: toMomentWithParsers(administeredDate).toDate(),
          locale,
          customFormat: dateFormatMar
        });
        const dosageDateDisplay = formatDate({
          date: toMomentWithParsers(dosageDate).toDate(),
          locale,
          customFormat: dateFormatMar
        });
        if (isAdministered) {
          return (
            <Box className="dosage-time" key={id}>
              <Box className="u-els-margin-right-1o4">
                <ELSIcon name="checkmark" size="1x" color="white" customClass="round-icon round-icon--administered" />
              </Box>
              {isShowEHRMar ? (
                <div className="dosage-time__text-ehr">{`${administeredDateDisplay} ${administeredTime} - ${firstInitial}${lastInitial ?? ''}`}</div>
              ) : (
                <ELSButton
                  type="link"
                  text={`${administeredDateDisplay} ${administeredTime} - ${firstInitial}${lastInitial ?? ''}`}
                  onClick={() => props.onDosageClick?.(row, item)}
                />
              )}
            </Box>
          );
        }
        // A held dosage is terminal like an administered one, but only for its own slot (SIMSNG-6033).
        // Read-only here: unholding goes through MarHoldUnholdSidebar on the held EHR row.
        if (isHeld) {
          const holdTimeDisplay = holdTime || holdTimeOffset;
          const holdInitials = [holdFirstInitial, holdLastInitial].filter(Boolean).join('');
          const heldLabel = [`${dosageDateDisplay} ${dosageTime} - Held${holdTimeDisplay ? ` ${holdTimeDisplay}` : ''}`, holdInitials].filter(Boolean).join(' - ');
          return (
            <Box className="dosage-time" key={id}>
              <Box className="u-els-margin-right-1o4">
                <ELSIcon name="pause-solid" size="1x" color="secondary" />
              </Box>
              <div className={cx('is-held', { 'dosage-time__text-ehr': isShowEHRMar })}>{heldLabel}</div>
            </Box>
          );
        }
        if (row.isDiscontinued || row.inactiveType === INACTIVE_TYPE.EXPIRED) {
          return (
            <Box className="dosage-time" key={id}>
              <div className="c-els-table__cell-content row--deleted">{`${dosageDateDisplay} ${dosageTime}`}</div>
            </Box>
          );
        }
        return (
          orderType !== ORDER_TYPES.PRN &&
          orderType !== ORDER_TYPES.STAT &&
          dosageTime !== 'Administer' && (
            <Box className="dosage-time" key={id}>
              {dosageDate && (
                <>
                  <div className="prefix u-els-margin-right-1o4">•</div>
                  {isShowEHRMar ? (
                    <div className="dosage-time__text-ehr">{`${administeredDateDisplay} ${administeredTime} - ${firstInitial}${lastInitial ?? ''}`}</div>
                  ) : (
                    <ELSButton
                      className={cx({ 'is-hold': row.isHold })}
                      type="link"
                      text={`${dosageDateDisplay} ${dosageTime}`}
                      onClick={() => row.inactiveType !== INACTIVE_TYPE.PAST_DUE && !row.isHold && !isShowEHRMar && props.onDosageClick?.(row, item)}
                    />
                  )}
                </>
              )}
            </Box>
          )
        );
      });
    const showAdmin = getIsShowAdmin(dosageTimesMapping, frequency, dosageTimes, props.hideAdministration, isInactiveOrder, isAnyAdministered, orderType);
    return (
      <>
        {!isShowEHRMar && showAdmin && (
          <ELSButton
            isDisabled={row.isHold || row.inactiveType}
            className={cx({ 'is-hold': row.isHold || row.inactiveType })}
            type="link"
            text="Administer"
            onClick={() => props.onDosageClick?.(row, undefined)}
          />
        )}
        {result}
      </>
    );
  };

  const getSingleDosageTimeRender = (firstDosageTime, orderStartTime) => {
    if (firstDosageTime?.isAdministered) {
      return firstDosageTime.administeredTime;
    }

    if (!firstDosageTime?.dosageTime || firstDosageTime?.dosageTime === 'Administer') {
      return orderStartTime;
    }

    return firstDosageTime?.dosageTime;
  };

  const heldRowRenderEHR = (row) => {
    const rowMoment = moment(row.dosageTimeTable, DATE_FORMAT);
    const heldTimes = row.dosageTimes.map((dosageTime) => dosageTime.dosageTime).join(' | ');
    return (
      <div className="dosage-times is-held">
        <div className="is-held__date">{rowMoment.format(DATE_FORMAT_MAR)}</div>
        <div className="dosage-times-list">
          <div className="dosage-times-item">
            <div className="time">{heldTimes}</div>
          </div>
        </div>
        <p className="dosage-times__day">{rowMoment.format('dddd')}</p>
      </div>
    );
  };

  const dosageTimesRenderEHR = (row) => {
    const { orderType, dosageTimeTable, originDosageTimes, inactiveType, status, orderStartTime } = row;
    const dosageTimesFilterByDate = originDosageTimes?.filter((dosageTime) => dosageTime.administeredDate === dosageTimeTable);

    if (row.isHeldRow) {
      return heldRowRenderEHR(row);
    }
    if (inactiveType || status) {
      return dosageTimesRender(row);
    }

    const onShowModal = () => {
      if (!modalService) return;
      const modalId = 'dosageTimesModal';
      modalService.openModal({
        modalId,
        content: <DosageTimesModal record={row} chartingTime={chartingTime} onClose={() => modalService.closeModal?.(modalId)} />
      });
    };

    const dayOfWeek = moment(dosageTimeTable, DATE_FORMAT).format('dddd');
    const formattedDosageDate = moment(dosageTimeTable, DATE_FORMAT).format(DATE_FORMAT_MAR);
    const firstDosageTime = dosageTimesFilterByDate?.[0];
    // The row's own dosageTimes are already narrowed to the day - and, after the held-row split,
    // exclude held slots. Re-deriving from originDosageTimes here would resurrect them.
    const dosageTimesByTableDate = row.dosageTimes ?? [];
    const isSingleDosageTime = (dosageTimesFilterByDate?.length ?? 0) <= 1 || orderType === ORDER_TYPES.STAT;

    const scheduleAction = (
      <div className="dosage-times-action">
        <ELSIcon prefix="gizmo" name="information" size="1x" align="top" color="secondary" />
        <ELSButton className="dosage-times-action__btn" text="View Schedule" type="debuttonize" onClick={onShowModal} />
      </div>
    );

    if (isSingleDosageTime) {
      const dosageTimeRender = getSingleDosageTimeRender(firstDosageTime, orderStartTime);

      return (
        <div className="dosage-times">
          <div className="dosage-times-list">
            <div className="dosage-times-item">
              {firstDosageTime?.isAdministered && (
                <div className="item-checked">
                  <ELSIcon name="confirmation-solid-circle" size="1x" color="confirm" />
                </div>
              )}
              <div className="time">{`${formattedDosageDate} | ${dosageTimeRender}`}</div>
            </div>
          </div>
          <p className={cx('dosage-times__day', { 'ml-20px': firstDosageTime?.isAdministered })}>{dayOfWeek}</p>
          {scheduleAction}
        </div>
      );
    }

    const dosageTimeRender = (dosageTime) => (!dosageTime?.dosageTime || dosageTime?.dosageTime === 'Administer' ? orderStartTime : dosageTime?.dosageTime);

    return (
      <div className="dosage-times">
        <div className="ml-20px">{formattedDosageDate}</div>
        <div className="dosage-times-list">
          {sortByDosageTime(dosageTimesByTableDate).map((dosageTime) => (
            <div key={dosageTime.id} className="dosage-times-item">
              <div className="item-checked">{dosageTime.isAdministered ? <ELSIcon name="confirmation-solid-circle" size="1x" color="confirm" /> : ''}</div>
              <div className="time">{dosageTime.isAdministered ? String(dosageTime?.administeredTime) : dosageTimeRender(dosageTime)}</div>
            </div>
          ))}
        </div>
        <p className="dosage-times__day ml-20px">{dayOfWeek}</p>
        {scheduleAction}
      </div>
    );
  };

  // The held row's only action is Unhold; Administer and Discontinue render disabled to keep the
  // Actions column aligned with its sibling rows (SIMSNG-6033).
  const heldRowActionRender = (row) => (
    <div className="mar-table__action-field">
      {isShowEHRMar && (
        <button disabled className="sng-button-icon-wrapper" type="button" aria-label="Administer">
          <ELSIcon color="n3" name="confirmation-outline-circle" size="1x" />
        </button>
      )}
      <button className="sng-button-icon-wrapper" type="button" aria-label="Unhold" onClick={() => handleUnHoldMedication(row)}>
        <ELSIcon color="secondary" name="pause-solid" size="1x" />
      </button>
      <button disabled className="sng-button-icon-wrapper" type="button" aria-label="Discontinue">
        <ELSIcon color="n3" name={isShowEHRMar ? 'close' : 'hand'} size="1x" />
      </button>
    </div>
  );

  const renderHoldButton = (row, isHold: boolean) => (
    <button className="sng-button-icon-wrapper" type="button" aria-label="Hold" onClick={() => (isHold ? handleUnHoldMedication(row) : handleHoldMedication(row))}>
      <ELSIcon color="secondary" name={isHold ? 'pause-solid' : 'pause-hollow'} size="1x" />
    </button>
  );

  const actionRender = (row) => {
    const { inactiveType, isHold, isDiscontinued, orderType, frequency, dosageTimes, isInactiveOrder, isAnyAdministered } = row;
    if (row.isHeldRow) {
      return heldRowActionRender(row);
    }
    const isAdminDisabled = isHold || inactiveType;
    const isItemDisabled = isDiscontinued || inactiveType === INACTIVE_TYPE.EXPIRED;
    // Judge from the row's own (day-narrowed, post-split) dosages - held siblings are not
    // administrable, so they must not keep the Administer button alive.
    const isAllDosageAdministered = (orderType === ORDER_TYPES.IV || orderType === 'Custom') && (dosageTimes ?? []).every((dosageTime) => dosageTime.isAdministered);
    const showAdmin = getIsShowAdmin(dosageTimesMapping, frequency, dosageTimes, props.hideAdministration, isInactiveOrder, isAnyAdministered, orderType);

    const isAdministerDisabled = isAdminDisabled || isItemDisabled || (isAllDosageAdministered && !showAdmin);

    return (
      <div className="mar-table__action-field">
        {isShowEHRMar && (
          <button disabled={isAdministerDisabled} className="sng-button-icon-wrapper" type="button" aria-label="Administer" onClick={() => props.onDosageClick?.(row, undefined)}>
            <ELSIcon color={isAdministerDisabled ? 'n3' : 'secondary'} name="confirmation-outline-circle" size="1x" />
          </button>
        )}
        {renderHoldButton(row, isHold)}
        <button disabled={isHold} className="sng-button-icon-wrapper" type="button" aria-label="Discontinue" onClick={() => handleDiscontinueMedication({ ...row })}>
          <ELSIcon color={isHold ? 'n3' : 'secondary'} name={isShowEHRMar ? 'close' : 'hand'} size="1x" />
        </button>
      </div>
    );
  };

  const informationRender = (row) => {
    return (
      <div className="mar-table_information-field">
        {isShowEHRMar ? (
          <DrugNameBtnRedirect drugName={row.drugName} showLink />
        ) : (
          <span>
            {row.drugName}
            <ClinicalKeyDrugInformation drugName={row.drugName} showLink />
          </span>
        )}
      </div>
    );
  };

  const orderRender = (order) => {
    const dateTimeDisplay = formatDate({
      date: toMomentWithFormat(order, Object.values(DateTimeFormatByLocale)).toDate(),
      locale,
      includeTime: true,
      customFormat: isShowEHRMar ? DATE_TIME_FORMAT_MAR : undefined
    });
    const dayOfWeekDisplay = formatDate({
      date: toMomentWithFormat(order, Object.values(DateTimeFormatByLocale)).toDate(),
      locale,
      customFormat: 'dddd'
    });

    if (!isShowEHRMar) {
      return <span>{dateTimeDisplay}</span>;
    }

    return (
      <>
        <p>{dateTimeDisplay}</p>
        <p className="day-of-week">{dayOfWeekDisplay}</p>
      </>
    );
  };

  // Now only show status for Discontinued status
  const renderStatus = (row) => {
    if (row.isDiscontinued) {
      return isShowEHRMar ? convertDateFormat(row.status) : row.status;
    }
    return '';
  };

  const filterByViewData = ({ dosageTimes, orderStart, orderStop }, date) => {
    const hasDosageTimesWithDate = dosageTimes?.some((dosage) => dosage.dosageDate === date);
    const startDate = toMomentWithFormat(orderStart, Object.values(DateFormatByLocale));
    const stopDate = toMomentWithFormat(orderStop, Object.values(DateFormatByLocale));
    return hasDosageTimesWithDate || toMomentWithFormat(date, Object.values(DateFormatByLocale)).isBetween(startDate, stopDate, MOMENT_UNIT_DAYS, MOMENT_INCLUSIVITY_ALL);
  };

  const mappingDosageTimeByViewDate = (item, date) => {
    const isInactiveOrder = item.inactiveType || item.isDiscontinued;
    const dosageTimes = isInactiveOrder
      ? item.dosageTimes
      : item.dosageTimes?.filter(
          (dosageTime) =>
            formatDate({
              date: toMomentWithParsers(dosageTime.dosageDate).toDate(),
              locale
            }) === date
        );
    return { ...item, originDosageTimes: item.dosageTimes, dosageTimes };
  };

  const mappingIsAnyAdministered = (item) => {
    const isAnyAdministered = item.dosageTimes?.some((dosageTime) => dosageTime.isAdministered);
    return { ...item, originDosageTimes: item.dosageTimes, isAnyAdministered };
  };

  const splitHeldRows = (item) => {
    if (!isShowEHRMar || item.inactiveType || item.status) {
      return [item];
    }
    const heldDosageTimes = item.dosageTimes?.filter((dosageTime) => dosageTime.isHeld) ?? [];
    if (heldDosageTimes.length === 0) {
      return [item];
    }
    const availableDosageTimes = item.dosageTimes.filter((dosageTime) => !dosageTime.isHeld);
    const heldRow = { ...item, dosageTimes: heldDosageTimes, isHeldRow: true };
    return availableDosageTimes.length ? [heldRow, { ...item, dosageTimes: availableDosageTimes }] : [heldRow];
  };

  const filteredData = data
    .map((item) => mappingIsAnyAdministered(item))
    .map((item) => mappingDosageTimeByViewDate(item, isShowEHRMar ? item.dosageTimeTable : viewDate))
    .flatMap(splitHeldRows)
    .filter((item) => (isShowEHRMar ? item : filterByViewData(item, viewDate)));

  const contentTooltip = useMemo(() => {
    return isShowEHRMar ? 'To Administer, Hold or Discontinue' : 'To hold or discontinue';
  }, [isShowEHRMar]);

  const orderDayCount = new Set(filteredData.map((item) => `${item.fragmentId}|${item.dosageTimeTable ?? ''}`)).size;

  useEffect(() => {
    if (getCount) {
      getCount(orderDayCount);
    }
  }, [orderDayCount, getCount]);

  return (
    <div className="mar-table">
      <HorizontalScrollableContainer>
        <ELSDataTable data={filteredData} columnHeaderCss={columnHeaderCss}>
          <column field="drugName" header="Drug Name" customRender={informationRender} />
          {!isShowEHRMar && <column field="orderStart" header="Order Start" sortable customRender={(row) => orderRender(row.orderStart)} />}
          {!isShowEHRMar && <column field="orderStop" header="Order Stop" sortable customRender={(row) => orderRender(row.orderStop)} />}
          <column field="dose" header="Dose" />
          <column field="route" header="Route" />
          {!hideFrequency && <column field="frequency" header="Frequency" />}
          <column
            field="dosageTimes"
            header={isShowEHRMar ? 'Dosage Day & Time(s)' : 'Dosage Time(s)'}
            customRender={(row) => (isShowEHRMar ? dosageTimesRenderEHR(row) : dosageTimesRender(row))}
          />
          {isShowEHRMar && <column field="orderStart" header="Order Start" sortable customRender={(row) => orderRender(row.orderStart)} />}
          {isShowEHRMar && <column field="orderStop" header="Order Stop" sortable customRender={(row) => orderRender(row.orderStop)} />}
          {!hideAction && (
            <column
              field="action"
              header={
                <>
                  <span>Actions </span>
                  <ELSTooltip content={contentTooltip} showClose>
                    <ELSIcon prefix="gizmo" name="information" size="1x" align="top" color="secondary" />
                  </ELSTooltip>
                </>
              }
              customRender={actionRender}
            />
          )}
          {!hideStatus && <column field="status" header="Status" customRender={renderStatus} />}
        </ELSDataTable>
      </HorizontalScrollableContainer>
      {filteredData.length === 0 && (
        <Box className="qa-table__no-data" mt mb2>
          <span>{AppMessage.NO_TABLE_DATA}</span>
        </Box>
      )}
    </div>
  );
};
MarTable.displayName = 'MarTable';
export default MarTable;
